import { useCallback, useEffect } from "react";
import {
  Button,
  DatePicker,
  Division,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  Select,
  Span,
  Textarea,
  useToast,
} from "@ir/ui";
import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { IUpdatePromoProgramParams } from "@/core/repositories/promotion/IPromoDetail.repository";
import { useWorkspace } from "@/hooks";
import { usePromotionEdit, usePromotions } from "../../hooks";
import EditProgramDetailsCol from "./EditProgramDetailsCol";
import { useProgramDetailsOptions } from "./hooks/useProgramDetailsOptions";

interface EditProgramDetailsProps {
  promoDetail: PromoDetail;
}

const toSelectOption = (label?: string | null, value?: string | number | null) => {
  if (!label || value === null || value === undefined) {
    return null;
  }
  return {
    label,
    value: value.toString(),
  };
};

const buildProgramFormValues = (promoDetail: PromoDetail) => ({
  promoType: toSelectOption(promoDetail.type.promoTypeName, promoDetail.type.promoTypeId),
  purchaser: toSelectOption(promoDetail.purchaser.purchaserName, promoDetail.purchaser.purchaserId),
  retailer: toSelectOption(promoDetail.retailer?.retailerName, promoDetail.retailer?.retailerId),
  startDate: promoDetail.startDate,
  endDate: promoDetail.endDate,
  notes: promoDetail.notes ?? "",
  committedBudget: promoDetail.financials.committedBudget ?? null,
});

const EditProgramDetails = ({ promoDetail }: EditProgramDetailsProps) => {
  const [EditProgramDetailsForm] = Form.useForm();
  const { setEditMode, updatePromoProgram, isPromoProgramUpdating } = usePromotionEdit();
  const { getPromotions } = usePromotions();
  const { settings } = useWorkspace();
  const toast = useToast();
  const {
    purchaserOptions,
    promoTypeOptions,
    retailerOptions,
    isPurchasersLoading,
    isPromoTypesLoading,
    isRetailersLoading,
  } = useProgramDetailsOptions();

  useEffect(() => {
    EditProgramDetailsForm.setFieldsValue(buildProgramFormValues(promoDetail));
  }, [promoDetail, EditProgramDetailsForm]);

  const handleCancel = useCallback(() => {
    EditProgramDetailsForm.resetFields();
    setEditMode(null);
  }, [EditProgramDetailsForm, setEditMode]);

  const refreshPromotionsList = useCallback(() => {
    void getPromotions({ isLoading: false, isRefreshing: true });
  }, [getPromotions]);

  const handleSubmit = async () => {
    const validateFields = await EditProgramDetailsForm.validateFields({
      dirty: true,
    });

    const params: IUpdatePromoProgramParams = {
      promoEventId: promoDetail.promoEventId,
      promoTypeId: Number.parseInt(validateFields.promoType.value),
      purchaserId: Number.parseInt(validateFields.purchaser.value),
      retailerId: Number.parseInt(validateFields.retailer.value),
      startDate: validateFields.startDate,
      endDate: validateFields.endDate,
      notes: validateFields.notes && validateFields.notes.length > 0 ? validateFields.notes : "",
      committedBudget: validateFields.committedBudget ? Number.parseFloat(validateFields.committedBudget) : null,
    };

    try {
      await updatePromoProgram(params);
      toast.success({ content: "Promotion details updated" });
      refreshPromotionsList();
    } catch (error) {
      toast.error({
        content: error instanceof Error ? error.message : "Failed to update promotion details",
      });
    }
  };

  const startDate = Form.useWatch("startDate", EditProgramDetailsForm);
  const endDate = Form.useWatch("endDate", EditProgramDetailsForm);

  return (
    <Form form={EditProgramDetailsForm} className="editProgramDetails" onFinish={handleSubmit}>
      <Row className="flex-1 px-4 pb-4">
        <EditProgramDetailsCol className="mb-3">
          <Form.Item
            name="promoType"
            isList
            label="Promo type"
            rules={[{ required: true, message: "Promo type is required" }]}
          >
            <Select size="small" options={promoTypeOptions} isClearable={false} isLoading={isPromoTypesLoading} />
          </Form.Item>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol className="mb-3">
          <Form.Item
            name="purchaser"
            isList
            label="Purchaser"
            rules={[{ required: true, message: "Purchaser is required" }]}
          >
            <Select size="small" options={purchaserOptions} isClearable={false} isLoading={isPurchasersLoading} />
          </Form.Item>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol className="mb-3">
          <Form.Item
            name="retailer"
            isList
            label="Retailer"
            rules={[{ required: true, message: "Retailer is required" }]}
          >
            <Select size="small" options={retailerOptions} isClearable={false} isLoading={isRetailersLoading} />
          </Form.Item>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol className="mb-3">
          <Division className="mb-2">
            <Span className="d-flex text-sm font-medium text-grey-700">
              Start Date: <sub className="form-item-required">*</sub>
            </Span>
          </Division>
          <Division className="display-picker display-picker-single">
            <Form.Item
              name="startDate"
              label="Start date"
              rules={[{ required: true, message: "Start date is required" }]}
            >
              <DatePicker
                dateFormat={settings?.displayDateFormat}
                shouldCloseOnInteractOutside={true}
                isNonModal={true}
                maxValue={endDate}
              />
            </Form.Item>
          </Division>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol className="mb-3">
          <Division className="mb-2">
            <Span className="d-flex text-sm font-medium text-grey-700">
              End Date: <sub className="form-item-required">*</sub>
            </Span>
          </Division>
          <Division className="display-picker display-picker-single">
            <Form.Item name="endDate" label="End date" rules={[{ required: true, message: "End date is required" }]}>
              <DatePicker
                dateFormat={settings?.displayDateFormat}
                minValue={startDate}
                shouldCloseOnInteractOutside={true}
                isNonModal={true}
              />
            </Form.Item>
          </Division>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol className="mb-3">
          <Form.Item name="committedBudget" label="Committed Budget">
            <Input size="small" type="number" className="no-helper-input" />
          </Form.Item>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol md={12} className="mb-3">
          <Form.Item name="notes" label="Notes">
            <Textarea size="small" maxLength={500} showCharCount />
          </Form.Item>
        </EditProgramDetailsCol>

        <EditProgramDetailsCol md={12} className="mb-0">
          <Form.Item>
            <Flex justifyContent="end" className="custom-button-container">
              <Button
                size="medium"
                color="grey"
                variant="clear"
                onPress={handleCancel}
                isDisabled={isPromoProgramUpdating}
              >
                <Icon name={Icons.Close} slot="start" size="medium" />
                Cancel
              </Button>

              <Button size="medium" variant="outline" type="submit" isDisabled={isPromoProgramUpdating}>
                <Icon
                  name={isPromoProgramUpdating ? Icons.Loading : Icons.Check}
                  slot="start"
                  size="medium"
                  className={isPromoProgramUpdating ? "icon-fast-loading" : ""}
                />
                Submit
              </Button>
            </Flex>
          </Form.Item>
        </EditProgramDetailsCol>
      </Row>
    </Form>
  );
};

export default EditProgramDetails;
